/** The cookie every surface records the visitor's consent choice in. */
export const CONSENT_COOKIE = 'pb_consent';
/**
 * Persistent account-linked attribution does not use the narrow aggregate
 * analytics exemptions. Country codes also cover EEA members absent from the
 * Cloudflare EU flag; the flag independently admits newly added EU members.
 */
const EU_EEA_COUNTRIES = new Set([
    // EU-27
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
    'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI',
    'SK',
    // EEA-EFTA — the GDPR applies, and cf.isEUCountry never flags them
    'IS', 'LI', 'NO',
]);
/**
 * Consent-gated collection outside the EEA for the current combined analytics
 * and attribution purposes. These are not universal prohibitions on cookies.
 * Quebec is matched by subdivision; India's rule has an effective date.
 */
const OPT_IN_COUNTRIES = new Set([
    'GB', // United Kingdom — UK GDPR + PECR
    'CH', // Joined profiling requires a purpose/risk assessment before relying on opt-out.
    'BR', // Joined analytics/advertising has no established legitimate-interest assessment.
    'KR', // Account linkage falls outside non-identifying behavioral measurement.
    'JP', // Identified third-party advertising exports require separate safeguards.
    'ZA', // Alternative lawful bases must cover every purpose of the shared identifier.
]);
/** DPDP processing/consent provisions commence on 13 May 2027 in India. */
const INDIA_CONSENT_START_MS = Date.parse('2027-05-13T00:00:00+05:30');
/** Opt-out jurisdictions: default-on first-party measurement, opt-out link shown. */
const OPT_OUT_COUNTRIES = new Set([
    'US', // CCPA/CPRA + ~19 state laws
    'CA', // Canada — PIPEDA (Quebec is opt-in, handled below)
]);
/**
 * The codes Cloudflare uses when it has no country to report: `XX` for an
 * unresolvable address, `T1` for a Tor exit. Both mean "unknown", and unknown
 * is the case `classifyRegion` fails closed on — treating them as country codes
 * would instead route those visitors to the most permissive regime.
 */
const UNKNOWN_COUNTRY_CODES = new Set(['XX', 'T1']);
/**
 * The visitor's country as an uppercase code, or null when the edge could not
 * place them. The one reading of the country: `classifyRegion` reads it to
 * place the visitor in a jurisdiction and `decidePageview` sends it when it is
 * a two-letter code, so a pageview is never recorded under a country its
 * consent verdict did not read.
 */
export function normalizeCountry(country) {
    const code = country?.trim().toUpperCase();
    return code && !UNKNOWN_COUNTRY_CODES.has(code) ? code : null;
}
export function classifyRegion({ country, regionCode, isEU }) {
    if (isEU)
        return 'opt-in';
    const code = normalizeCountry(country);
    // Fail closed. A visitor we cannot place may be one the GDPR covers, and the
    // cost of the two mistakes is not symmetric: assuming opt-in loses a pageview
    // from someone who never had to consent, assuming `row` tracks — and cookies
    // — an EU visitor who never did. Unknown geography gets the strictest regime.
    if (!code)
        return 'opt-in';
    if (EU_EEA_COUNTRIES.has(code))
        return 'opt-in';
    if (OPT_IN_COUNTRIES.has(code))
        return 'opt-in';
    if (code === 'IN' && Date.now() >= INDIA_CONSENT_START_MS)
        return 'opt-in';
    // Quebec's Law 25 is prior-consent even though the rest of Canada is opt-out.
    if (code === 'CA' && regionCode === 'QC')
        return 'opt-in';
    if (OPT_OUT_COUNTRIES.has(code))
        return 'opt-out';
    return 'row';
}
/**
 * Whether a pageview may be tracked — and `pb_vid` minted — on this request.
 * Minting the visitor cookie is itself "tracking" under opt-in law, so this
 * single gate governs both the cookie and the server-to-server beacon.
 */
export function mayTrack({ region, consent }) {
    if (consent === 'denied')
        return false; // explicit opt-out always wins
    if (region === 'opt-in')
        return consent === 'granted'; // prior consent required
    return true; // opt-out / row: on by default (denied already excluded)
}
export function readConsent(value) {
    return value === 'granted' || value === 'denied' ? value : null;
}
export function parseCookies(header) {
    const out = {};
    const conflicts = new Set();
    if (!header)
        return out;
    // Split on ';' then trim: RFC 6265 prescribes '; ' but real clients/proxies
    // also send ';' without the space — splitting on '; ' alone would glue
    // 'a=1;pb_consent=denied' into one value and misread an explicit denial.
    for (const part of header.split(';')) {
        const trimmed = part.trim();
        if (!trimmed)
            continue;
        const eq = trimmed.indexOf('=');
        if (eq === -1)
            continue;
        const key = trimmed.slice(0, eq);
        const value = trimmed.slice(eq + 1);
        // A host-scoped cookie can coexist with the domain cookie. No ordering
        // can make an explicit denial disappear or choose a conflicting identity.
        if (key === CONSENT_COOKIE && (value === 'denied' || out[key] === 'denied')) {
            out[key] = 'denied';
            continue;
        }
        if (conflicts.has(key))
            continue;
        if (Object.hasOwn(out, key) && out[key] !== value) {
            delete out[key];
            conflicts.add(key);
            continue;
        }
        Object.defineProperty(out, key, { value, enumerable: true, configurable: true, writable: true });
    }
    return out;
}
